import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, extractError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ListVideo, Plus, Pencil, Trash2, Eye, EyeOff, Play, Search, ChevronRight,
  ArrowRight, PlayCircle, Layers, Check, ArrowLeft, ArrowUp, ArrowDown, X
} from "lucide-react";
import { toast } from "sonner";

const empty = { title: "", description: "", cover_url: "", video_ids: [], published: true };

// -------- List Page --------
export default function PlaylistsPage() {
  const { id } = useParams();
  if (id) return <PlaylistDetail id={id} />;
  return <PlaylistList />;
}

function PlaylistList() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [allVideos, setAllVideos] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    try {
      const [{ data: pls }, { data: vids }] = await Promise.all([
        api.get("/playlists"),
        isAdmin ? api.get("/videos") : Promise.resolve({ data: [] }),
      ]);
      setItems(pls);
      setAllVideos(vids);
    } catch (e) { toast.error(extractError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => (p.title || "").toLowerCase().includes(q) || (p.description || "").toLowerCase().includes(q));
  }, [items, search]);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || "", description: p.description || "",
      cover_url: p.cover_url || "", video_ids: p.video_ids || [], published: !!p.published,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("العنوان مطلوب"); return; }
    if (!form.video_ids.length) { toast.error("أضف فيديو واحداً على الأقل"); return; }
    try {
      if (editing) await api.patch(`/playlists/${editing.id}`, form);
      else await api.post("/playlists", form);
      toast.success(editing ? "تم التحديث" : "تم الإنشاء");
      setOpen(false); setForm(empty); setEditing(null); load();
    } catch (e) { toast.error(extractError(e)); }
  };
  const del = async (p) => {
    if (!confirm(`حذف قائمة "${p.title}" نهائياً؟`)) return;
    try { await api.delete(`/playlists/${p.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };
  const togglePublish = async (p) => {
    try {
      await api.patch(`/playlists/${p.id}`, {
        title: p.title, description: p.description || "", cover_url: p.cover_url || "",
        video_ids: p.video_ids || [], published: !p.published,
      });
      toast.success(p.published ? "تم إلغاء النشر" : "تم النشر"); load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const toggleVideo = (vid) => {
    setForm((f) => {
      const has = f.video_ids.includes(vid);
      return { ...f, video_ids: has ? f.video_ids.filter((x) => x !== vid) : [...f.video_ids, vid] };
    });
  };
  const moveVideo = (idx, dir) => {
    setForm((f) => {
      const arr = [...f.video_ids];
      const j = idx + dir;
      if (j < 0 || j >= arr.length) return f;
      [arr[idx], arr[j]] = [arr[j], arr[idx]];
      return { ...f, video_ids: arr };
    });
  };

  return (
    <div className="space-y-6" data-testid="playlists-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <Layers className="w-8 h-8 text-indigo-600" />
            قوائم التشغيل التعليمية
          </h1>
          <p className="text-slate-500 mt-2">سلاسل فيديو منتقاة للعائلات — تُشغَّل تباعاً بلقطة واحدة</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="h-12 gap-2 bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 shadow-lg" data-testid="new-playlist-btn">
            <Plus className="w-4 h-4" /> قائمة جديدة
          </Button>
        )}
      </div>

      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="بحث بالعنوان أو الوصف..." className="pr-10" data-testid="playlist-search" />
      </div>

      {filtered.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400">
          <Layers className="w-14 h-14 mx-auto mb-3 opacity-40" />
          <div className="font-bold">لا توجد قوائم تشغيل حتى الآن</div>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((p) => (
            <Card key={p.id} className="overflow-hidden border-slate-200 hover:shadow-2xl transition-all group" data-testid={`playlist-card-${p.id}`}>
              <button
                onClick={() => navigate(`/playlists/${p.id}`)}
                className="relative w-full aspect-video overflow-hidden block bg-gradient-to-br from-indigo-500 via-violet-600 to-fuchsia-600"
                data-testid={`open-playlist-${p.id}`}
              >
                {p.cover_url ? (
                  <img src={p.cover_url} alt={p.title} className="w-full h-full object-cover opacity-90 group-hover:scale-105 transition-transform" />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Layers className="w-20 h-20 text-white/60" />
                  </div>
                )}
                <div className="absolute inset-0 flex items-end justify-between p-4 bg-gradient-to-t from-black/70 via-transparent to-transparent">
                  <div className="text-white/90 text-xs font-black inline-flex items-center gap-1 bg-white/20 backdrop-blur rounded-full px-3 py-1">
                    <PlayCircle className="w-3.5 h-3.5" /> {p.video_count} فيديو
                  </div>
                  <div className="w-14 h-14 rounded-full bg-white shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                    <Play className="w-7 h-7 text-indigo-600 mr-0.5" fill="currentColor" />
                  </div>
                </div>
                {isAdmin && !p.published && (
                  <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/80 text-white text-[10px] font-black">
                    <EyeOff className="w-3 h-3" /> مسودة
                  </div>
                )}
              </button>
              <CardContent className="p-4">
                <h3 className="font-black text-slate-900 line-clamp-2 leading-tight">{p.title}</h3>
                {p.description && <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{p.description}</p>}
                <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                  <span>{p.author_name || ""}</span>
                  <span>{(p.created_at || "").slice(0, 10)}</span>
                </div>
                {isAdmin && (
                  <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                    <Button variant="outline" size="sm" onClick={() => openEdit(p)} className="gap-1.5" data-testid={`edit-playlist-${p.id}`}>
                      <Pencil className="w-3.5 h-3.5" /> تعديل
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => togglePublish(p)} className={`gap-1.5 ${p.published ? "text-amber-700 border-amber-200" : "text-emerald-700 border-emerald-200"}`} data-testid={`toggle-playlist-${p.id}`}>
                      {p.published ? <><EyeOff className="w-3.5 h-3.5" /> إلغاء</> : <><Eye className="w-3.5 h-3.5" /> نشر</>}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => del(p)} className="gap-1.5 text-rose-700 border-rose-200" data-testid={`delete-playlist-${p.id}`}>
                      <Trash2 className="w-3.5 h-3.5" /> حذف
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="playlist-dialog">
            <DialogHeader><DialogTitle className="text-2xl font-black">{editing ? "تعديل قائمة" : "قائمة تشغيل جديدة"}</DialogTitle></DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label className="font-bold">العنوان *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="p-title" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">الوصف</Label>
                <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="p-desc" />
              </div>
              <div className="space-y-1.5">
                <Label className="font-bold">صورة الغلاف (اختياري)</Label>
                <Input dir="ltr" type="url" value={form.cover_url} onChange={(e) => setForm({ ...form, cover_url: e.target.value })} placeholder="https://..." data-testid="p-cover" />
              </div>

              <div>
                <Label className="font-bold mb-2 block">
                  الفيديوهات في القائمة ({form.video_ids.length})
                </Label>
                {form.video_ids.length === 0 ? (
                  <div className="text-center py-6 text-slate-400 text-sm bg-slate-50 rounded-xl">لم يتم اختيار أي فيديو بعد</div>
                ) : (
                  <div className="space-y-2 max-h-56 overflow-y-auto pr-1">
                    {form.video_ids.map((vid, i) => {
                      const v = allVideos.find((x) => x.id === vid);
                      if (!v) return null;
                      return (
                        <div key={vid} className="flex items-center gap-2 bg-white border border-slate-200 rounded-xl p-2" data-testid={`selected-video-${i}`}>
                          <div className="w-8 h-8 shrink-0 rounded-full bg-indigo-100 text-indigo-700 font-black flex items-center justify-center text-sm">{i + 1}</div>
                          <div className="flex-1 min-w-0">
                            <div className="font-bold text-sm truncate">{v.title}</div>
                            <div className="text-[11px] text-slate-500">{v.category}</div>
                          </div>
                          <Button type="button" size="icon" variant="outline" onClick={() => moveVideo(i, -1)} disabled={i === 0} className="shrink-0" data-testid={`up-video-${i}`}>
                            <ArrowUp className="w-3.5 h-3.5" />
                          </Button>
                          <Button type="button" size="icon" variant="outline" onClick={() => moveVideo(i, 1)} disabled={i === form.video_ids.length - 1} className="shrink-0" data-testid={`down-video-${i}`}>
                            <ArrowDown className="w-3.5 h-3.5" />
                          </Button>
                          <Button type="button" size="icon" variant="outline" onClick={() => toggleVideo(vid)} className="shrink-0 text-rose-600 border-rose-200">
                            <X className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>

              <div>
                <Label className="font-bold mb-2 block">اختر من مكتبة الفيديوهات ({allVideos.length})</Label>
                <div className="max-h-64 overflow-y-auto border border-slate-200 rounded-xl p-2 space-y-1">
                  {allVideos.length === 0 ? (
                    <div className="p-6 text-center text-slate-400">لا توجد فيديوهات — أنشئ فيديو أولاً من مكتبة الفيديوهات</div>
                  ) : allVideos.map((v) => {
                    const selected = form.video_ids.includes(v.id);
                    return (
                      <button
                        type="button"
                        key={v.id}
                        onClick={() => toggleVideo(v.id)}
                        className={`w-full flex items-center gap-3 p-2 rounded-lg text-right transition-all ${selected ? "bg-indigo-50 border-2 border-indigo-300" : "bg-white border-2 border-transparent hover:bg-slate-50"}`}
                        data-testid={`pick-video-${v.id}`}
                      >
                        <div className={`w-8 h-8 shrink-0 rounded-lg flex items-center justify-center ${selected ? "bg-indigo-600 text-white" : "bg-slate-100 text-slate-400"}`}>
                          {selected ? <Check className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className={`font-bold text-sm truncate ${selected ? "text-indigo-900" : "text-slate-900"}`}>{v.title}</div>
                          <div className="text-[11px] text-slate-500">{v.category} • {v.source_type}</div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-bold text-slate-800">النشر الفوري</div>
                  <div className="text-xs text-slate-500">سيظهر لأولياء الأمور والمعلمين</div>
                </div>
                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="p-published" />
              </div>

              <Button onClick={save} className="w-full h-12 bg-gradient-to-l from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 gap-2" data-testid="save-playlist-btn">
                <Layers className="w-4 h-4" /> {editing ? "تحديث" : "إنشاء"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

// -------- Detail Page --------
function videoStreamUrl(v) {
  if (v.source_type === "upload" && v.storage_path) return `${API}/files/${v.storage_path}`;
  return v.embed_url || v.video_url || "";
}

function PlaylistDetail({ id }) {
  const navigate = useNavigate();
  const [pl, setPl] = useState(null);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    api.get(`/playlists/${id}`).then((r) => { setPl(r.data); setIdx(0); }).catch((e) => toast.error(extractError(e)));
  }, [id]);

  if (!pl) return <div className="p-12 text-center text-slate-400" data-testid="playlist-loading">جاري التحميل...</div>;

  const videos = pl.videos || [];
  const current = videos[idx];
  const isUpload = current?.source_type === "upload";
  // For iframe embeds add autoplay + enable JS API. For YouTube also use enablejsapi for onEnded.
  const iframeSrc = current ? (
    current.source_type === "youtube"
      ? `${current.embed_url}?autoplay=1&rel=0`
      : current.source_type === "vimeo"
        ? `${current.embed_url}?autoplay=1`
        : videoStreamUrl(current)
  ) : "";

  return (
    <div className="space-y-4" data-testid="playlist-detail-page">
      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" onClick={() => navigate("/playlists")} className="gap-1.5" data-testid="back-to-playlists">
          <ArrowRight className="w-4 h-4" /> رجوع
        </Button>
        <h1 className="text-2xl md:text-3xl font-black text-slate-900 flex items-center gap-2">
          <Layers className="w-6 h-6 text-indigo-600" /> {pl.title}
        </h1>
        <span className="text-xs font-black bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full inline-flex items-center gap-1">
          <PlayCircle className="w-3 h-3" /> {videos.length} فيديو
        </span>
      </div>
      {pl.description && <p className="text-slate-600 max-w-3xl">{pl.description}</p>}

      {videos.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400">لا توجد فيديوهات في هذه القائمة</CardContent></Card>
      ) : (
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-2 space-y-3">
            <Card className="overflow-hidden bg-black border-0" data-testid="playlist-player">
              <div className="aspect-video">
                {current && (isUpload ? (
                  <video
                    key={current.id}
                    src={iframeSrc}
                    controls
                    autoPlay
                    onEnded={() => setIdx((i) => Math.min(i + 1, videos.length - 1))}
                    className="w-full h-full"
                  />
                ) : (
                  <iframe
                    key={current.id}
                    src={iframeSrc}
                    title={current.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                ))}
              </div>
            </Card>
            {current && (
              <div className="bg-white rounded-xl border border-slate-200 p-4">
                <div className="text-xs font-black text-indigo-600 uppercase">فيديو {idx + 1} من {videos.length} — {current.category}</div>
                <h2 className="mt-1 font-black text-lg text-slate-900">{current.title}</h2>
                {current.description && <p className="mt-2 text-sm text-slate-600 leading-relaxed">{current.description}</p>}
                <div className="mt-3 flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setIdx((i) => Math.max(i - 1, 0))}
                    disabled={idx === 0}
                    className="gap-1"
                    data-testid="prev-video-btn"
                  >
                    <ArrowRight className="w-4 h-4" /> السابق
                  </Button>
                  <Button
                    onClick={() => setIdx((i) => Math.min(i + 1, videos.length - 1))}
                    disabled={idx >= videos.length - 1}
                    className="gap-1 bg-indigo-600 hover:bg-indigo-700"
                    data-testid="next-video-btn"
                  >
                    التالي <ArrowLeft className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </div>

          <div className="space-y-2 max-h-[70vh] overflow-y-auto pr-1" data-testid="playlist-sidebar">
            {videos.map((v, i) => (
              <button
                key={v.id}
                onClick={() => setIdx(i)}
                className={`w-full flex gap-3 p-2 rounded-xl text-right transition-all border-2 ${i === idx ? "bg-indigo-50 border-indigo-400" : "bg-white border-slate-100 hover:border-slate-300"}`}
                data-testid={`sidebar-video-${i}`}
              >
                <div className="relative w-28 aspect-video shrink-0 rounded-lg overflow-hidden bg-slate-900">
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center">
                      <Play className="w-6 h-6 text-white" fill="currentColor" />
                    </div>
                  )}
                  {i === idx && (
                    <div className="absolute inset-0 bg-indigo-600/50 flex items-center justify-center">
                      <PlayCircle className="w-7 h-7 text-white" fill="currentColor" />
                    </div>
                  )}
                  <div className="absolute bottom-1 left-1 w-5 h-5 rounded-full bg-black/70 text-white text-[10px] font-black flex items-center justify-center">
                    {i + 1}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className={`font-bold text-sm line-clamp-2 leading-tight ${i === idx ? "text-indigo-900" : "text-slate-900"}`}>{v.title}</div>
                  <div className="text-[11px] text-slate-500 mt-1">{v.category}</div>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
